import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Bell } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { bottomNav } from "@/lib/nav";
import { api } from "@/lib/api";
import { ModeToggle, useDarkMode } from "@/components/ModeToggle";
import { listenForeground } from "@/lib/push";
import { toast } from "sonner";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { ViewAsBanner } from "@/components/ViewAsBanner";

let listening = false;

function BottomNav({ dark }) {
  const { user } = useAuth();
  const items = bottomNav(user?.role);
  return (
    <nav data-testid="bottom-nav" className={`md:hidden fixed bottom-0 inset-x-0 z-40 flex justify-around px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t backdrop-blur-xl ${dark ? "dark-surface border-white/10" : "bg-white/95 border-violet-100"}`}>
      {items.map((it) => (
        <NavLink key={it.to + it.label} to={it.to} end={it.to === "/dashboard"} data-testid={`bottom-nav-${it.label.toLowerCase()}`}
          className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl text-[11px] font-semibold transition-colors ${isActive ? (dark ? "bg-white/15 text-white" : "text-[color:var(--brand)]") : (dark ? "text-white/60" : "text-slate-500")}`}>
          <it.icon size={20} /> {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

function MobileTopBar({ dark }) {
  const [unread, setUnread] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    Promise.all([api.get("/messages/unread-count"), api.get("/notifications/unread-count")])
      .then(([m, n]) => setUnread(m.data.count + n.data.count)).catch(() => {});
  }, [pathname]);
  useEffect(() => {
    if (!listening) { listening = true; listenForeground((n) => toast(n.title || "SPARKORA EDU", { description: n.body })); }
  }, []);
  return (
    <header className={`md:hidden sticky top-0 z-30 text-white px-4 py-3 flex items-center justify-between ${dark ? "dark-surface" : "brand-header"}`}>
      <Logo onDark size="sm" />
      <div className="flex items-center gap-1">
      <ModeToggle />
      <Link to="/notifications" data-testid="topbar-bell" className="relative p-1.5">
        <Bell size={22} />
        {unread > 0 && <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />}
      </Link>
      </div>
    </header>
  );
}

export function AppShell({ children, bare = false }) {
  const dark = useDarkMode();
  return (
    <div data-testid="app-shell" className={`min-h-screen flex ${dark ? "admin-dark dark-bg" : "bg-[#F6F4FC]"}`}>
      <Sidebar />
      <main className="flex-1 min-w-0 pb-24 md:pb-0">
        <ViewAsBanner />
        {!bare && <MobileTopBar dark={dark} />}
        <div className={bare ? "w-full" : "px-4 sm:px-6 lg:px-10 py-6 lg:py-8 max-w-7xl mx-auto w-full"}>{children}</div>
      </main>
      <BottomNav dark={dark} />
      <ForcePasswordChange />
    </div>
  );
}
